import {useState, useMemo} from "react";
import {Alert, Button, Container, Row} from "react-bootstrap";
import {toDateString} from "../service/JobService";
import JobStatusToggleGroup from "./JobStatusToggleGroup";
import {
    CLEANING_BOARD_MODE,
    TASK_BOARD_MODE,
    cleaningBoardConfig,
    taskBoardConfig,
} from "./scheduler/boardConfigs";
import SchedulerBacklogColumn from "./scheduler/SchedulerBacklogColumn";
import SchedulerCard from "./scheduler/SchedulerCard";
import SchedulerMobileCardAction from "./scheduler/SchedulerMobileCardAction";
import SchedulerMobileAssignAction from "./scheduler/SchedulerMobileAssignAction";
import SchedulerMobileView from "./scheduler/SchedulerMobileView";
import SchedulerToolbar from "./scheduler/SchedulerToolbar";
import SchedulerWorkerLane from "./scheduler/SchedulerWorkerLane";
import TaskModal from "./scheduler/TaskModal";
import TaskStatusTabs from "./scheduler/TaskStatusTabs";
import UpdateJobModal from "./UpdateJobModal";
import useSchedulerMobileLayout from "./scheduler/useSchedulerMobileLayout";
import {
    buildWorkerWorkloadCounts,
    isTaskJob,
    mergeSchedulerJobsById,
    sortSchedulerJobs,
    stringToDate,
} from "./scheduler/schedulerUtils";
import useSchedulerBoardData from "./scheduler/useSchedulerBoardData";
import useSchedulerResources from "./scheduler/useSchedulerResources";
import useBlockedAgencyStatus from "./scheduler/useBlockedAgencyStatus";
import "./scheduler/Scheduler.css";
import {
    getSchedulerDragData,
    SCHEDULER_BACKLOG_ZONE,
    SCHEDULER_WORKER_ZONE,
} from "./scheduler/schedulerDragDrop";

const classicCleaningBoardConfig = {
    ...cleaningBoardConfig,
    statusStorageKey: "radioValue",
    legacyStatusStorageKey: null,
    workerStorageKey: null,
};

const classicTaskBoardConfig = {
    ...taskBoardConfig,
    workerStorageKey: null,
};

function formatSameDayAssignmentLabel(workerNames = []) {
    if (workerNames.length === 0) {
        return "";
    }

    if (workerNames.length === 1) {
        return `Day job: ${workerNames[0]}`;
    }

    return `Day job: ${workerNames[0]} +${workerNames.length - 1}`;
}

function convertTimeToMinutes(timeValue) {
    const normalizedValue = String(timeValue || "").trim();
    if (!normalizedValue) {
        return null;
    }

    const [hours, minutes] = normalizedValue.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return null;
    }

    return (hours * 60) + minutes;
}

function getEarlierTime(leftTime, rightTime) {
    if (!leftTime) {
        return rightTime || "";
    }
    if (!rightTime) {
        return leftTime;
    }

    return convertTimeToMinutes(leftTime) <= convertTimeToMinutes(rightTime) ? leftTime : rightTime;
}

function getLaterTime(leftTime, rightTime) {
    if (!leftTime) {
        return rightTime || "";
    }
    if (!rightTime) {
        return leftTime;
    }

    return convertTimeToMinutes(leftTime) >= convertTimeToMinutes(rightTime) ? leftTime : rightTime;
}

export default function Scheduler() {
    const now = new Date();
    const [editingJobId, setEditingJobId] = useState(null);
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(() => {
        const schedulerDate = localStorage.getItem("schedulerDate");
        return schedulerDate || toDateString(now);
    });
    const [searchText, setSearchText] = useState(() => localStorage.getItem("searchText") || "");
    const [activeBoardMode, setActiveBoardMode] = useState(CLEANING_BOARD_MODE);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isJobModalOpen, setIsJobModalOpen] = useState(false);
    const isMobileLayout = useSchedulerMobileLayout();
    const schedulerResources = useSchedulerResources();
    const blockedAgencyStatus = useBlockedAgencyStatus();
    const cleaningBoard = useSchedulerBoardData({
        boardConfig: classicCleaningBoardConfig,
        selectedDate,
        searchText,
        fieldWorkerMap: schedulerResources.fieldWorkerMap,
        fieldSupervisorMap: schedulerResources.fieldSupervisorMap,
    });
    const taskBoard = useSchedulerBoardData({
        boardConfig: classicTaskBoardConfig,
        selectedDate,
        searchText,
        fieldWorkerMap: schedulerResources.fieldWorkerMap,
        fieldSupervisorMap: schedulerResources.fieldSupervisorMap,
    });
    const activeBoard = activeBoardMode === TASK_BOARD_MODE ? taskBoard : cleaningBoard;
    const sharedActiveWorkerId = activeBoardMode === TASK_BOARD_MODE
        ? taskBoard.activeWorkerId
        : cleaningBoard.activeWorkerId;
    const mixedWorkerJobs = useMemo(() => {
        return sortSchedulerJobs(mergeSchedulerJobsById(
            cleaningBoard.workerJobs.map((job) => ({
                ...job,
                __schedulerCardType: isTaskJob(job) ? "task" : "cleaning",
            })),
            taskBoard.workerJobs.map((job) => ({...job, __schedulerCardType: "task"})),
        ));
    }, [cleaningBoard.workerJobs, taskBoard.workerJobs]);
    const mixedWorkerCounts = useMemo(() => {
        return buildWorkerWorkloadCounts(mergeSchedulerJobsById(
            cleaningBoard.workerVisibleJobs,
            taskBoard.workerVisibleJobs,
        ));
    }, [cleaningBoard.workerVisibleJobs, taskBoard.workerVisibleJobs]);
    const todayWorkerIds = useMemo(() => {
        if (!schedulerResources.businessDate) {
            return new Set();
        }
        const todayJobs = mergeSchedulerJobsById(cleaningBoard.jobs, taskBoard.jobs);
        return new Set(todayJobs
            .filter((job) => job.status !== "Cancelled"
                && String(job.date || "") === schedulerResources.businessDate
                && job.cos_job_worker != null
                && String(job.cos_job_worker) !== "")
            .map((job) => String(job.cos_job_worker)));
    }, [cleaningBoard.jobs, schedulerResources.businessDate, taskBoard.jobs]);
    const isViewingToday = Boolean(
        schedulerResources.businessDate && selectedDate === schedulerResources.businessDate,
    );
    const sameDayTaskAssignments = useMemo(() => {
        return cleaningBoard.jobs.reduce((propertyAssignments, job) => {
            if (!isTaskJob(job) && job.status !== "Cancelled" && job.property_id != null && job.property_id !== "") {
                const propertyId = String(job.property_id);
                const workerName = schedulerResources.fieldWorkerMap[job.cos_job_worker] || "Unassigned";
                const existingAssignment = propertyAssignments[propertyId] || {
                    workerNames: [],
                    hasSameDayJob: false,
                    checkOut: "",
                    checkIn: "",
                };
                const existingWorkerNames = existingAssignment.workerNames;
                propertyAssignments[propertyId] = {
                    workerNames: existingWorkerNames.includes(workerName)
                        ? existingWorkerNames
                        : [...existingWorkerNames, workerName],
                    hasSameDayJob: true,
                    checkOut: getEarlierTime(existingAssignment.checkOut, job.check_out),
                    checkIn: getLaterTime(existingAssignment.checkIn, job.check_in),
                };
            }
            return propertyAssignments;
        }, {});
    }, [cleaningBoard.jobs, schedulerResources.fieldWorkerMap]);
    const cleaningStatusCountsWithTask = useMemo(() => ({
        ...cleaningBoard.statusCounts,
        Task: (taskBoard.statusCounts.Pending || 0)
            + (taskBoard.statusCounts.PendingWithDate || 0)
            + (taskBoard.statusCounts.Scheduled || 0),
    }), [cleaningBoard.statusCounts, taskBoard.statusCounts]);
    const boardHeight = Math.max(activeBoard.visibleBacklogJobs.length, mixedWorkerJobs.length, 1) * 420;
    const isBoardLoading = cleaningBoard.isLoading || taskBoard.isLoading;
    const isLoading = isBoardLoading || blockedAgencyStatus.isLoading || schedulerResources.isLoading;
    const schedulerErrorMessage = cleaningBoard.errorMessage || taskBoard.errorMessage;

    function isJobAgencyBlocked(job) {
        const agencyId = job?.cos_customer_id;
        return agencyId != null
            && agencyId !== ""
            && blockedAgencyStatus.blockedAgencyIds.has(String(agencyId));
    }

    function resolveJobAssignment(jobId) {
        const taskJob = taskBoard.jobs.find((job) => String(job.cos_job_id) === String(jobId));
        const cleaningJob = cleaningBoard.jobs.find((job) => String(job.cos_job_id) === String(jobId));
        const job = taskJob || cleaningJob;

        if (!job) {
            return null;
        }

        return {
            board: isTaskJob(job) ? taskBoard : cleaningBoard,
            job,
        };
    }

    function handleDateSelect(nextDate) {
        setSelectedDate(nextDate);
        localStorage.setItem("schedulerDate", nextDate);
    }

    function handleDateOffset(deltaDays) {
        if (deltaDays === 0) {
            handleDateSelect(schedulerResources.businessDate || toDateString(new Date()));
            return;
        }

        const nextDate = stringToDate(selectedDate);
        nextDate.setDate(nextDate.getDate() + deltaDays);

        handleDateSelect(toDateString(nextDate));
    }

    function handleSearchTextChange(nextSearchText) {
        setSearchText(nextSearchText);
        localStorage.setItem("searchText", nextSearchText);
    }

    function handleCleaningStatusChange(nextStatus) {
        setActiveBoardMode(CLEANING_BOARD_MODE);
        cleaningBoard.setSelectedStatus(nextStatus);
    }

    function handleTaskModeClick() {
        setActiveBoardMode(TASK_BOARD_MODE);
    }

    function handleTaskStatusChange(nextStatus) {
        setActiveBoardMode(TASK_BOARD_MODE);
        taskBoard.setSelectedStatus(nextStatus);
    }

    function openCreateTaskModal() {
        setEditingTaskId(null);
        setIsTaskModalOpen(true);
    }

    function openEditJobModal(jobId) {
        setEditingJobId(jobId);
        setIsJobModalOpen(true);
    }

    function closeJobModal() {
        setEditingJobId(null);
        setIsJobModalOpen(false);
    }

    function openEditTaskModal(taskId) {
        setEditingTaskId(taskId);
        setIsTaskModalOpen(true);
    }

    function closeTaskModal() {
        setEditingTaskId(null);
        setIsTaskModalOpen(false);
    }

    function refreshSchedulerBoards() {
        return Promise.all([
            cleaningBoard.refreshBoard(),
            taskBoard.refreshBoard(),
        ]);
    }

    async function refreshAllBoards() {
        const refreshes = [
            refreshSchedulerBoards(),
            blockedAgencyStatus.refresh(),
        ];
        if (isViewingToday) {
            refreshes.push(schedulerResources.refreshUsers());
        }
        await Promise.all(refreshes);
    }

    async function assignJobToWorker(jobId, workerId) {
        if (blockedAgencyStatus.isLoading) {
            return false;
        }

        const assignment = resolveJobAssignment(jobId);
        if (!assignment || isJobAgencyBlocked(assignment.job)) {
            return false;
        }

        const wasAssigned = await assignment.board.assignWorker(
            jobId,
            workerId,
            assignment.job,
        );
        if (wasAssigned) {
            await refreshSchedulerBoards();
        }

        return wasAssigned;
    }

    async function moveJobToBacklog(jobId) {
        const assignment = resolveJobAssignment(jobId);
        if (!assignment) {
            return false;
        }

        const wasAssigned = await assignment.board.assignWorker(jobId, isTaskJob(assignment.job)
            ? classicTaskBoardConfig.defaultWorkerId
            : classicCleaningBoardConfig.defaultWorkerId, assignment.job);
        if (wasAssigned) {
            await refreshSchedulerBoards();
        }

        return wasAssigned;
    }

    async function handleBacklogDrop(event) {
        const {jobId, sourceZone} = getSchedulerDragData(event);
        if (sourceZone !== SCHEDULER_WORKER_ZONE || !jobId) {
            return;
        }

        await moveJobToBacklog(jobId);
    }

    async function handleWorkerDrop(event) {
        const {jobId, sourceZone} = getSchedulerDragData(event);
        if (sourceZone !== SCHEDULER_BACKLOG_ZONE || !jobId) {
            return;
        }

        await assignJobToWorker(jobId, sharedActiveWorkerId);
    }

    function handleWorkerChange(nextWorkerId) {
        cleaningBoard.setActiveWorkerId(nextWorkerId);
        taskBoard.setActiveWorkerId(nextWorkerId);
    }

    function renderSchedulerCard(job, dragSourceZone, mobileAction = null) {
        return (
            <SchedulerCard
                key={job.cos_job_id}
                job={job}
                fieldSupervisorMap={schedulerResources.fieldSupervisorMap}
                fieldWorkerMap={schedulerResources.fieldWorkerMap}
                isLoading={isBoardLoading || (dragSourceZone === SCHEDULER_BACKLOG_ZONE
                    ? blockedAgencyStatus.isLoading
                    : false)}
                onJobUpdated={activeBoard.updateJobInState}
                onEditJob={() => (isTaskJob(job)
                    ? openEditTaskModal(job.cos_job_id)
                    : openEditJobModal(job.cos_job_id))}
                sameDayAssignmentLabel={formatSameDayAssignmentLabel(
                    sameDayTaskAssignments[String(job.property_id)]?.workerNames || [],
                )}
                sameDaySchedule={sameDayTaskAssignments[String(job.property_id)] || null}
                dragSourceZone={dragSourceZone}
                isAgencyBlocked={isJobAgencyBlocked(job)}
                mobileAction={mobileAction}
            />
        );
    }

    function renderBacklogCard(job) {
        const isBlocked = isJobAgencyBlocked(job);

        return renderSchedulerCard(
            job,
            SCHEDULER_BACKLOG_ZONE,
            <SchedulerMobileAssignAction
                disabled={isBlocked || blockedAgencyStatus.isLoading}
                fieldWorkerArray={schedulerResources.fieldWorkerArray}
                isLoading={isBoardLoading || blockedAgencyStatus.isLoading}
                title={isBlocked ? "Agency blocked — cannot assign to worker" : undefined}
                onConfirm={(workerId) => assignJobToWorker(job.cos_job_id, workerId)}
                workerWorkloadCounts={mixedWorkerCounts}
            />,
        );
    }

    function renderWorkerCard(job) {
        return renderSchedulerCard(
            job,
            SCHEDULER_WORKER_ZONE,
            <SchedulerMobileCardAction
                label="Move to Backlog"
                disabled={isBoardLoading}
                isLoading={isBoardLoading}
                onClick={() => moveJobToBacklog(job.cos_job_id)}
                variant="outline-secondary"
            />,
        );
    }

    const taskBacklogHeader = (
        <div className="scheduler-task-header">
            <TaskStatusTabs
                selectedStatus={taskBoard.selectedStatus}
                statusCounts={taskBoard.statusCounts}
                onChange={handleTaskStatusChange}
            />
            <Button onClick={openCreateTaskModal}>New Task</Button>
        </div>
    );

    return (
        <Container fluid>
            <SchedulerToolbar
                isLoading={isLoading}
                onDateOffset={handleDateOffset}
                onDateSelect={handleDateSelect}
                onRefresh={refreshAllBoards}
                onSearchTextChange={handleSearchTextChange}
                searchText={searchText}
                selectedDate={selectedDate}
                statusControls={(
                    <JobStatusToggleGroup
                        radioValue={activeBoardMode === CLEANING_BOARD_MODE ? cleaningBoard.selectedStatus : ""}
                        setRadioValue={handleCleaningStatusChange}
                        statusCounts={cleaningStatusCountsWithTask}
                        isTaskSelected={activeBoardMode === TASK_BOARD_MODE}
                        onTaskClick={handleTaskModeClick}
                    />
                )}
            />
            {blockedAgencyStatus.errorMessage ? (
                <Alert className="py-2 mb-2" variant="warning">
                    {blockedAgencyStatus.errorMessage}
                </Alert>
            ) : null}
            {schedulerErrorMessage ? (
                <Alert className="py-2 mb-2" variant="danger">
                    {schedulerErrorMessage}
                </Alert>
            ) : null}
            {schedulerResources.errorMessage ? (
                <Alert className="py-2 mb-2" variant="warning">
                    {schedulerResources.errorMessage}
                </Alert>
            ) : null}
            <hr />
            {isMobileLayout ? (
                <SchedulerMobileView
                    activeWorkerId={sharedActiveWorkerId}
                    backlogCount={activeBoard.visibleBacklogJobs.length}
                    backlogCards={activeBoard.visibleBacklogJobs.map((job) => renderBacklogCard(job))}
                    backlogEmptyMessage={activeBoardMode === TASK_BOARD_MODE
                        ? classicTaskBoardConfig.emptyMessage
                        : classicCleaningBoardConfig.emptyMessage}
                    backlogHeader={activeBoardMode === TASK_BOARD_MODE ? taskBacklogHeader : null}
                    fieldWorkerArray={schedulerResources.fieldWorkerArray}
                    onActiveWorkerChange={handleWorkerChange}
                    onClearFilter={() => handleSearchTextChange("")}
                    showWorkerLocation={isViewingToday}
                    todayWorkerIds={todayWorkerIds}
                    workerCount={mixedWorkerJobs.length}
                    workerCards={mixedWorkerJobs.map((job) => renderWorkerCard(job))}
                    workerEmptyMessage="No jobs assigned to this worker"
                    workerWorkloadCounts={mixedWorkerCounts}
                />
            ) : (
                <Row>
                <SchedulerBacklogColumn
                    boardHeight={boardHeight}
                    hasJobs={activeBoard.visibleBacklogJobs.length > 0}
                    header={activeBoardMode === TASK_BOARD_MODE ? taskBacklogHeader : null}
                    emptyMessage={activeBoardMode === TASK_BOARD_MODE
                        ? classicTaskBoardConfig.emptyMessage
                        : classicCleaningBoardConfig.emptyMessage}
                    onClearFilter={() => handleSearchTextChange("")}
                    onDropJob={handleBacklogDrop}
                >
                    {activeBoard.visibleBacklogJobs.map((job) => (
                        renderSchedulerCard(job, SCHEDULER_BACKLOG_ZONE)
                    ))}
                </SchedulerBacklogColumn>
                <SchedulerWorkerLane
                    activeWorkerId={sharedActiveWorkerId}
                    boardHeight={boardHeight}
                    fieldWorkerArray={schedulerResources.fieldWorkerArray}
                    fieldWorkerMap={schedulerResources.fieldWorkerMap}
                    onActiveWorkerChange={handleWorkerChange}
                    onDropJob={handleWorkerDrop}
                    showWorkerLocation={isViewingToday}
                    todayWorkerIds={todayWorkerIds}
                    workerWorkloadCounts={mixedWorkerCounts}
                >
                    {mixedWorkerJobs.map((job) => (
                        renderSchedulerCard(job, SCHEDULER_WORKER_ZONE)
                    ))}
                </SchedulerWorkerLane>
                </Row>
            )}
            <TaskModal
                fieldWorkerArray={schedulerResources.fieldWorkerArray}
                initialDate={selectedDate}
                onClose={closeTaskModal}
                onSaved={async () => {
                    closeTaskModal();
                    await refreshSchedulerBoards();
                }}
                show={isTaskModalOpen}
                taskId={editingTaskId}
            />
            <UpdateJobModal
                jobId={editingJobId}
                onClose={closeJobModal}
                onSaved={async () => {
                    closeJobModal();
                    await refreshSchedulerBoards();
                }}
                show={isJobModalOpen}
            />
        </Container>
    );
}
